import React, { useState } from 'react';
import { Box, Stack, HStack, Input, Image, Button, useToast } from '@chakra-ui/react';
import { FaUpload } from 'react-icons/fa';
import axios from 'axios';
import { CustomText } from '../../atoms';
import { ToggleAtom } from '../../atoms/CustomToggle';
import { SectionData } from '../../../interfaces';
import { useSettings } from '../../../stateManagement/SettingsContext';
import { log } from '../../../utils';

function convertImageToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
}

interface SectionSettingsMoleculeProps {
  configsForData: SectionData[] | null;
  configs: {
    showSectionGrid: boolean;
  };
  onConfigChange: (key: string, value: any) => void;
}

export const SectionsSettingsMolecule: React.FC<SectionSettingsMoleculeProps> = ({
  configsForData,
  configs,
  onConfigChange,
}) => {
  const [showSectionGrid, setShowSectionGrid] = useState(configs.showSectionGrid);
  const [previews, setPreviews] = useState<{ [sectionId: string]: string }>({});
  const [files, setFiles] = useState<{ [sectionId: string]: File }>({});
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();
  const { adminApiKey, settings } = useSettings();

  const handleUploadAll = async () => {
    setIsUploading(true);

    try {
      // eslint-disable-next-line no-console
      // console.log('Testing', files);
      log(settings?.configs.showLogs, 'Testing', files);
      const uploadPromises = Object.entries(files).map(async ([sectionId, file]) => {
        const base64String = await convertImageToBase64(file);

        const payload = [
          {
            name: `${sectionId}.png`,
            type: 'image',
            path: `${process.env.REACT_APP_BLOB_CONTAINER_NAME}/section-images`,
            data: base64String,
          },
        ];

        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          // url: 'https://catalysthc.ms.helpshift.live/api/fe-config',
          url: 'https://demo-hc-api.ms.helpshift.live/api/fe-config',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${adminApiKey ?? ''}`,
          },
          data: JSON.stringify(payload),
        };

        const response = await axios.request(config);

        if (!(response.status >= 200 && response.status < 300)) {
          throw new Error(`Upload failed for section ${sectionId}`);
        }
      });

      await Promise.all(uploadPromises);

      toast({
        title: 'Upload successful!',
        description: 'All section images have been uploaded.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while uploading images.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box height="80vh" overflowY="auto" border="1px solid #e2e8f0" p={4} borderRadius="md">
      {/* Toggle Switch */}
      <Box
        p={4}
        borderWidth="1px"
        borderRadius="md"
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <ToggleAtom
          justifyContent="space-between"
          label="Show Section Wise Articles"
          value={showSectionGrid}
          onChange={value => {
            setShowSectionGrid(value);
            onConfigChange('showSectionGrid', value);
          }}
        />
      </Box>

      {/* Upload Button Below Toggle, Left Aligned */}
      <Box mb={4} pl={4}>
        <Button colorScheme="blue" onClick={handleUploadAll} isLoading={isUploading}>
          Upload All
        </Button>
      </Box>

      {/* Section Cards */}
      <Stack spacing={4}>
        {configsForData?.map(section => (
          <Box
            key={section.section_id}
            p={4}
            borderWidth="1px"
            borderRadius="md"
            backgroundColor="#f7fafc"
          >
            <HStack justifyContent="space-between" alignItems="center" mb={3}>
              <CustomText fontWeight="bold">{section.section_name}</CustomText>
              <Box position="relative" width="fit-content">
                <Input
                  type="file"
                  accept="image/*"
                  opacity={0}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const imageUrl = URL.createObjectURL(file);
                      setPreviews(prev => ({ ...prev, [section.section_id]: imageUrl }));
                      setFiles(prev => ({ ...prev, [section.section_id]: file }));
                    }
                  }}
                />
                <Box
                  width="40px"
                  height="40px"
                  borderRadius="md"
                  bg="gray.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                >
                  <FaUpload />
                </Box>
              </Box>
            </HStack>

            {previews[section.section_id] && (
              <Image
                src={previews[section.section_id]}
                alt="Preview"
                width="100%"
                maxHeight="200px"
                objectFit="contain"
                borderRadius="md"
                border="1px solid #ccc"
              />
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
